import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  LearningPathCardSkeleton,
  LearningPathCardVariants,
  LearningPathHorizontalCard,
  LearningPathVerticalCard,
} from '@/components';
import { Routes } from '@/constants';
import { Link } from '@/containers';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';

import {
  LearningPathsList,
  LearningPathsListVariants,
  LpBreadcrumbs,
  useLearningPathsData,
} from './components';

const REFERRER = {
  page: EVENT_CONSTANTS.PAGE.LEARNING_PATHS,
};

export const LearningPathsHome = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));

  const {
    isLoading,
    sortedInProgressLps,
    latestLps,
    recommendedLps,
    organizationName,
  } = useLearningPathsData();

  return (
    <Box>
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        {isLg && <LpBreadcrumbs />}
        <Typography variant="h1">Learning Paths</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {(isLoading || sortedInProgressLps.length > 0) && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}>
              <Typography variant="h3">In Progress</Typography>
              <Box sx={{ flexShrink: 0 }}>
                <Link
                  onClick={() => {
                    AnalyticsService.learningPathViewAllClicked({
                      section: 'in_progress',
                    });
                    navigate(Routes.LEARNING_PATHS_IN_PROGRESS);
                  }}>
                  View All
                </Link>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                },
              }}>
              {isLoading
                ? Array.from({ length: 3 }).map((_, index) => (
                    <Box
                      key={index}
                      sx={{ flex: 1, containerType: 'inline-size' }}>
                      <LearningPathCardSkeleton
                        variant={LearningPathCardVariants.HORIZONTAL}
                      />
                    </Box>
                  ))
                : sortedInProgressLps.slice(0, isXl ? 3 : 2).map(lp => (
                    <Box
                      key={lp.groupId}
                      sx={{ flex: 1, containerType: 'inline-size' }}>
                      <LearningPathHorizontalCard
                        learningPath={lp}
                        referrer={REFERRER}
                      />
                    </Box>
                  ))}
            </Box>
          </Box>
        )}
        {(isLoading || recommendedLps.length > 0) && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}>
              <Box>
                <Typography variant="h3">Recommended</Typography>
                <Typography
                  variant="body3SemiBold"
                  sx={{ color: 'text.light' }}>
                  Hand-picked for you by the team at {organizationName}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <Link
                  onClick={() => {
                    AnalyticsService.learningPathViewAllClicked({
                      section: 'recommended',
                    });
                    navigate(Routes.LEARNING_PATHS_RECOMMENDED);
                  }}>
                  View All
                </Link>
              </Box>
            </Box>
            {isMd ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isLoading
                  ? Array.from({ length: isXl ? 4 : 3 }).map((_, index) => (
                      <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                        <LearningPathCardSkeleton
                          stretch
                          variant={LearningPathCardVariants.VERTICAL}
                        />
                      </Box>
                    ))
                  : recommendedLps.slice(0, isXl ? 4 : 3).map(lp => (
                      <Box
                        key={lp.groupId}
                        sx={{ flex: 1, minWidth: 0, maxWidth: 300 }}>
                        <LearningPathVerticalCard
                          stretch
                          learningPath={lp}
                          referrer={REFERRER}
                        />
                      </Box>
                    ))}
              </Box>
            ) : (
              <LearningPathsList
                learningPaths={recommendedLps.slice(0, isXl ? 4 : 3)}
                variant={LearningPathsListVariants.HORIZONTAL}
                organizationName={organizationName}
                isLoading={isLoading}
                skeletonCount={isXl ? 4 : 3}
              />
            )}
          </Box>
        )}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}>
            <Typography variant="h3" sx={{ mb: 1 }}>
              Latest
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <Link
                onClick={() => {
                  AnalyticsService.learningPathViewAllClicked({
                    section: 'latest',
                  });
                  navigate(Routes.LEARNING_PATHS_TOPICS);
                }}>
                View All
              </Link>
            </Box>
          </Box>
          <LearningPathsList
            learningPaths={latestLps.slice(0, isMd ? 10 : 3)}
            variant={LearningPathsListVariants.LIST}
            organizationName={organizationName}
            isLoading={isLoading}
            skeletonCount={isMd ? 10 : 3}
          />
        </Box>
      </Box>
    </Box>
  );
};
